import { z } from 'zod';

export const tenantRoles = ['OWNER', 'ADMIN', 'AGENT', 'VIEWER'] as const;
export type TenantRole = (typeof tenantRoles)[number];

export const managementRoles: readonly TenantRole[] = ['OWNER', 'ADMIN'];
export const operationsRoles: readonly TenantRole[] = ['OWNER', 'ADMIN', 'AGENT'];

export const hasRole = (actual: TenantRole, allowed: readonly TenantRole[]): boolean =>
  allowed.includes(actual);

const requiredText = (max: number) => z.string().trim().min(1).max(max);

export const loginSchema = z.strictObject({
  email: z
    .email()
    .max(320)
    .transform((value) => value.toLowerCase()),
  password: z.string().min(12).max(256),
});

export const agentInputSchema = z.strictObject({
  displayName: requiredText(100),
  greeting: requiredText(500),
  defaultLocale: z.enum(['id', 'en']).optional(),
  greetingId: z.string().trim().max(500).optional(),
  greetingEn: z.string().trim().max(500).optional(),
  instructions: z.string().trim().max(10_000),
  primaryColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  isActive: z.boolean(),
  contactCaptureEnabled: z.boolean().optional(),
  humanHandoffEnabled: z.boolean().optional(),
  webSearchEnabled: z.boolean().optional(),
  allowedSearchDomains: z
    .array(
      z
        .string()
        .trim()
        .toLowerCase()
        .max(253)
        .regex(/^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/),
    )
    .max(20)
    .optional(),
});

export const webSearchConfigSchema = z
  .object({
    webSearchEnabled: z.boolean(),
    allowedSearchDomains: agentInputSchema.shape.allowedSearchDomains.unwrap(),
  })
  .refine((value) => !value.webSearchEnabled || value.allowedSearchDomains.length > 0, {
    message: 'Web Search requires at least one allowed search domain',
    path: ['allowedSearchDomains'],
  });

export const allowedOriginSchema = z
  .url()
  .max(2_048)
  .transform((value) => new URL(value).origin)
  .refine((value) => value.startsWith('https://') || value.startsWith('http://localhost:'), {
    message: 'Origins must use HTTPS, except localhost development origins',
  });

export const publicMessageSchema = z.strictObject({
  requestId: z.uuid().optional(),
  conversationId: z.string().cuid().optional(),
  message: requiredText(4_000),
  locale: z.enum(['id', 'en']).optional(),
});

export const captureLeadSchema = z
  .strictObject({
    name: z.string().trim().min(1).max(120).optional(),
    email: z.email().max(320).optional(),
    phone: z.string().trim().min(5).max(40).optional(),
    visitorMessage: z.string().trim().max(2_000).optional(),
    consent: z.literal(true),
    idempotencyKey: z.uuid(),
  })
  .refine((value) => Boolean(value.name || value.email || value.phone), {
    message: 'At least one contact detail is required',
  });

export const handoffSchema = z.strictObject({
  reason: z.string().trim().min(1).max(1_000),
  idempotencyKey: z.uuid(),
});

export const uploadRequestSchema = z.strictObject({
  agentId: z.string().cuid(),
  filename: requiredText(255),
  mimeType: z.enum([
    'application/pdf',
    'text/plain',
    'text/markdown',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ]),
  byteSize: z
    .number()
    .int()
    .positive()
    .max(30 * 1024 * 1024),
  checksum: z
    .string()
    .regex(/^[a-f0-9]{64}$/i)
    .optional(),
});

export type LoginInput = z.infer<typeof loginSchema>;
export type AgentInput = z.infer<typeof agentInputSchema>;
export type CaptureLeadInput = z.infer<typeof captureLeadSchema>;
export type HandoffInput = z.infer<typeof handoffSchema>;
export type UploadRequest = z.infer<typeof uploadRequestSchema>;

export interface ApiErrorShape {
  error: { code: string; message: string; requestId: string; details?: unknown };
}

export interface AuthenticatedUser {
  id: string;
  email: string;
  displayName: string;
  tenantId: string;
  tenantName: string;
  role: TenantRole;
}

export { citationSegments, safeCitationUrl, type Citation } from './citations';
